import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UploadOutlined } from "@ant-design/icons";
import CustomCard from "../../components/CustomCard/CustomCard";
import { useSelector, useDispatch } from "react-redux";
import useSearchableTable from "../../hooks/useSearchableTable";
import useModuleContext from "../../hooks/useModuleContext";
import useUserPrivileges from "../../hooks/useUserPrivileges";
import useDocDownloadError from "../../hooks/useDocDownloadError";
import TableHeader from "../../components/CustomTable/TableHeader";
import DealsTable from "./Components/DealsTable";
import { downloadPageHandle, generateStatusTableTitle } from "../../helpers/helpers";
import { DEAL_STATUS_LABEL_MAP, DEAL_STATUS, DEAL_CATEGORY_IDS } from "../../constants/constants";
import { PERM_CODE, DEAL_STATUS_PRIVILEGE_MAP } from "../../constants/PermissionCodes";
import "./Deals.scss";
import { dealsView, count } from "../../redux/slices/dealsSlice";
import { getStatusList } from "../../redux/slices/EnqMngtSlice";

const IS_DEAL_STATUS_IDS = Object.values(DEAL_STATUS).map((s) => s.ID);
const DEFAULT_DEAL_CATEGORY_ID = "DCT1000001";
const DEFAULT_DEAL_CATEGORY_NAME = "Invoice Financing";

const DEFAULT_DEAL_STATUSES = [
  DEAL_STATUS.PENDING.ID,
  DEAL_STATUS.APPROVED.ID,
];

const buildStatusTableTitle = ({ defaultTitle = "" } = {}) => defaultTitle || "";

const Deals = ({
  dealCategoryId = DEFAULT_DEAL_CATEGORY_ID,
  dealCategoryName = DEFAULT_DEAL_CATEGORY_NAME,
  paramPrefix = "",
  onDealUpload,
  hasDealUploadPrivilege: hasDealUploadPrivilegeProp,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { dealView, loading, CountData } = useSelector((state) => state?.deals);
  const { statusList } = useSelector((state) => state?.enqMngt);
  const docLoading = useDocDownloadError();
  const [columnControl, setColumnControl] = useState(null);
  const { userPrivileges, allowedStatusIds } = useUserPrivileges(DEAL_STATUS_PRIVILEGE_MAP);

  const hasDealUploadPrivilege =
    hasDealUploadPrivilegeProp !== undefined
      ? Boolean(hasDealUploadPrivilegeProp)
      : Boolean(userPrivileges?.includes(PERM_CODE.IUACC));

  const handleDealUpload = useCallback(() => {
    if (onDealUpload) {
      onDealUpload();
      return;
    }
    if (
      dealCategoryId === DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE ||
      dealCategoryName === "Supply Chain Financing" ||
      dealCategoryName === "Supply Chain Finance"
    ) {
      navigate("/supply-chain-upload");
    } else {
      navigate("/invoice-upload");
    }
  }, [onDealUpload, dealCategoryId, dealCategoryName, navigate]);

  const defaultStatus = useMemo(() => {
    const preferred = DEFAULT_DEAL_STATUSES.filter((id) =>
      allowedStatusIds.includes(id)
    );
    return preferred.length > 0 ? preferred : null;
  }, [allowedStatusIds]);

  const {
    selectedStatus,
    handleStatusChange,
    searchText,
    handleSearchChange,
    pagination,
    applyDefaultStatus,
  } = useModuleContext({
    moduleKey: `deals_${dealCategoryId}`,
    isMultiStatus: true,
    allowedStatusIds,
    defaultStatus,
    paramPrefix,
  });



  useEffect(() => {
    const fetchStatus = async () => {
      const res = await dispatch(getStatusList("deals"));
      if (res.payload?.statusCode === 200) {
        const preferred = DEFAULT_DEAL_STATUSES.filter((id) =>
          allowedStatusIds.includes(id)
        );
        const fallback =
          preferred.length > 0
            ? preferred
            : allowedStatusIds.length > 0
              ? [allowedStatusIds[0]]
              : [];
        applyDefaultStatus(fallback);
      }
    };
    fetchStatus();
  }, [dispatch, allowedStatusIds, applyDefaultStatus]);

  useEffect(() => {
    if (selectedStatus !== null && selectedStatus !== undefined) {
      dispatch(dealsView({ selectedStatus, dealCategoryId }));
    }
  }, [dispatch, selectedStatus, dealCategoryId]);

  useEffect(() => {
    dispatch(count("DEALS"));
  }, [dispatch]);

  const { filteredData: searchFilteredData } =
    useSearchableTable(dealView, searchText, handleSearchChange);

  const filteredData = useMemo(() => {
    if (Array.isArray(selectedStatus) && selectedStatus.length === 0) return [];
    return searchFilteredData;
  }, [searchFilteredData, selectedStatus]);

  const filteredStatusList = useMemo(() => {
    const augmentedStatusList = (statusList || []).slice();
    return augmentedStatusList.filter((s) => allowedStatusIds.includes(s.statusId));
  }, [statusList, allowedStatusIds]);

  const statusListWithCounts = useMemo(
    () => filteredStatusList.map((item) => {
      const isDeal = IS_DEAL_STATUS_IDS.includes(item.statusId);
      const matchedCount = CountData?.find(
        (c) => c.statusId === item.statusId && c.dealCategoryId === dealCategoryId,
      );
      return {
        ...item,
        count: isDeal ? Number(matchedCount?.count || 0) : undefined,
      };
    }),
    [filteredStatusList, CountData, dealCategoryId],
  );

  const handleDownload = useCallback(
    (type) => downloadPageHandle({
      dispatch,
      type,
      screenName: "deals",
      dealType: dealCategoryName,
      dealCategoryId,
      statusId: selectedStatus,
    }),
    [dispatch, selectedStatus, dealCategoryName, dealCategoryId],
  );

  const handleDownloadPdf = useCallback(() => handleDownload("pdf"), [handleDownload]);
  const handleDownloadExcel = useCallback(() => handleDownload("excel"), [handleDownload]);

  const tableTitle = useMemo(
    () =>
      (generateStatusTableTitle || buildStatusTableTitle)({
        defaultTitle: "Deals",
      }),
    [],
  );

  return (
    <CustomCard>
      <TableHeader
        title={tableTitle}
        searchText={searchText}
        setSearchText={handleSearchChange}
        isDropdown={true}
        dropdownData={statusListWithCounts}
        selectedStatus={selectedStatus}
        handleStatusChange={handleStatusChange}
        statusDisplayNames={DEAL_STATUS_LABEL_MAP}
        isDownloadRequired={true}
        onDownloadPdf={handleDownloadPdf}
        onDownloadExcel={handleDownloadExcel}
        downloadLoading={docLoading}
        columnControl={columnControl}
        btnName={hasDealUploadPrivilege ? "Deal Upload" : undefined}
        btnIcon={hasDealUploadPrivilege ? <UploadOutlined /> : undefined}
        handleBtnOnClick={hasDealUploadPrivilege ? handleDealUpload : undefined}
      />

      <DealsTable
        tableData={filteredData}
        selectedStatus={selectedStatus}
        pagination={pagination}
        dealCategoryId={dealCategoryId}
        loading={loading}
        onColumnControlChange={setColumnControl}
      />
    </CustomCard>
  );
};

export default Deals;